// Utility functions for variant configs
// This file has NO imports to avoid circular dependencies

// Check if section data is empty or has no meaningful content
const isDataEmpty = (data: any): boolean => {
  if (!data) return true;
  if (typeof data !== 'object') return false;
  
  const keys = Object.keys(data);
  if (keys.length === 0) return true;
  
  const meaningfulKeys = keys.filter(key => {
    if (key === 'id') return false;
    const value = data[key];
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
  
  return meaningfulKeys.length === 0;
};

/**
 * Resolves section background URL for admin preview.
 * - Non-empty Redux value wins (live upload / edit).
 * - Redux `null` means user cleared the file in the form.
 * - Missing key in Redux falls back to persisted API data (avoid form default "" wiping URL).
 * - Legacy Redux `""` still falls back to persisted when persisted has a URL (stale defaults).
 */
const resolveSectionBackgroundImage = (
  redux: Record<string, any> | null | undefined,
  persisted: Record<string, any> | null | undefined
): string => {
  const rHas =
    redux &&
    typeof redux === "object" &&
    Object.prototype.hasOwnProperty.call(redux, "section_background_image");
  const r = rHas ? redux!.section_background_image : undefined;
  const p = persisted?.section_background_image;

  const rStr = r != null && typeof r === "string" ? r.trim() : "";
  const pStr = p != null && typeof p === "string" ? String(p).trim() : "";

  if (rStr) return rStr;
  if (rHas && r === null) return "";
  if (rHas && r === "") return pStr || "";
  if (pStr) return pStr;
  return "";
};

const mergePersistedWithReduxForPreview = (
  persisted: Record<string, any>,
  redux: Record<string, any>
) => {
  const merged = { ...persisted, ...redux };
  merged.section_background_image = resolveSectionBackgroundImage(
    redux,
    persisted
  );
  return merged;
};

// Get preview data with priority: Redux state > section data > empty object
export const getPreviewData = (
  section: any,
  reduxState: any,
  isSelected: boolean
) => {
  const persisted =
    section?.data &&
    typeof section.data === "object" &&
    !isDataEmpty(section.data)
      ? section.data
      : null;

  // If selected, prefer Redux for live editing, but keep persisted FILE URLs when Redux never set them
  if (isSelected && reduxState && !isDataEmpty(reduxState)) {
    if (persisted) {
      return mergePersistedWithReduxForPreview(persisted, reduxState);
    }
    return reduxState;
  }

  // If section has data, use it
  if (persisted) {
    return persisted;
  }

  // Return empty object as fallback
  return {};
};

/**
 * Activate all metadata items by setting status to true.
 * Metadata columns are MySQL JSON read with `raw: true`, so they can surface as
 * a JSON string (or a legacy object) instead of an array — coerce so the CMS
 * preview never crashes on a bad shape.
 */
export const activateMetadata = (items: any) => {
  let rows = items;

  if (typeof rows === "string") {
    try {
      rows = JSON.parse(rows);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(rows)) return [];

  return rows.map((item) => ({ ...item, status: true }));
};

/**
 * CMS + MySQL booleans often arrive as 0/1. `v !== false` is wrong for 0.
 * Explicit off: false, 0, "0", "false". Missing value uses defaultOn (match legacy default-on).
 */
export const isCmsShowFlagOn = (
  raw: unknown,
  defaultOn: boolean = true,
): boolean => {
  if (raw === false || raw === 0) return false;
  if (raw === true || raw === 1) return true;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "0" || s === "false" || s === "no" || s === "off") return false;
    if (s === "1" || s === "true" || s === "yes" || s === "on") return true;
  }
  return defaultOn;
};

